/**
 * FR-020…029 · Danh sách tên miền — bám bố cục `domains.html`:
 * page-head · grid g-4 (thẻ số liệu) · scope-note · card (card-head toolbar +
 * table-wrap > table.tbl + card-foot) · modal thêm tên miền (BR-5).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, thanhApiError, useLoiApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../lib/i18n';
import { DOMAIN_STATUS_CHIP } from '../lib/status';
import { avatarTone, initialsOf, useFormat } from '../lib/format';
import type {
  AvailableConnections,
  CfAccountOption,
  Connection,
  CreateDomainResponse,
  Domain,
  DomainListResponse,
  DomainSyncResult,
  UserOption,
  VerifyNsResult,
} from '../lib/types';
import { Icon } from '../components/Icon';
import { SearchBox } from '../components/SearchBox';
import { Pagination } from '../components/Pagination';
import { SortButton } from '../components/SortButton';
import { ShareCountCell } from '../components/ShareCountCell';
import { usePhanTrangUrl } from '../lib/phan-trang';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { Modal } from '../components/Modal';
import { Field, SelectField } from '../components/Field';
import { AsyncSection, EmptyState, SkeletonCard } from '../components/States';
import { useToast } from '../components/Toast';
import { ImportZonesModal } from './ImportZonesModal';

/**
 * Bộ lọc trên thanh tab. `sync_paused` KHÔNG phải một `domain_status` — nó nằm
 * ở trục khác (OQ-234), nên khi chọn nó ta gửi tham số `syncPaused` riêng chứ
 * không gửi `status`. Gộp vào một enum sẽ mất đúng những tổ hợp cần tìm: một
 * tên miền có thể vừa `active` trên Cloudflare vừa ngừng đồng bộ với DNSHub.
 */
const FILTERS = ['all', 'active', 'pending', 'paused', 'error', 'sync_paused'] as const;
type Filter = (typeof FILTERS)[number];

export function DomainsPage() {
  const { formatRelative, formatNumber } = useFormat();
  const t = useT();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const isAdmin = can('dom.viewall');
  const canAdd = can('dom.add');

  // FR-023 — giữ trạng thái lọc trên URL query.
  const [params] = useSearchParams();
  const status = (params.get('status') as Filter | null) ?? 'all';
  const q = params.get('q') ?? '';
  const plan = params.get('plan') ?? '';
  const sort = params.get('sort') ?? 'name:asc';
  /*
   * OQ-265 — lọc theo kết nối, đến từ trang Quản lý kết nối.
   *
   * `GET /domains` đã nhận `connectionId` từ M4 và `visibleDomains()` đã áp nó
   * vào truy vấn; thứ thiếu là lối đi ở giao diện. Con số ở cột "Tên miền" bên
   * trang Kết nối nay dẫn thẳng sang đây, nên người dùng kiểm chứng được nó gồm
   * những tên miền nào thay vì phải tin một con số trần.
   */
  const connectionId = params.get('connectionId') ?? '';
  const { page, perPage, vaThamSo, datTrang, datSoDong, datLoc } = usePhanTrangUrl();

  const [search, setSearch] = useState(q);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [created, setCreated] = useState<CreateDomainResponse | null>(null);
  const [toDelete, setToDelete] = useState<Domain | null>(null);

  // FR-022 — debounce 250 ms cho ô tìm kiếm.
  useEffect(() => {
    const tuKhoa = search.trim();
    /*
     * OQ-263 — CHỐT CHẶN. Không đổi thì không chạm URL.
     *
     * `setParams` đổi identity mỗi lần URL đổi (react-router bọc nó bằng
     * `useCallback(..., [navigate, searchParams])`), nên effect này bị đánh
     * thức sau MỌI thao tác chuyển trang. Thiếu dòng return dưới đây, thân
     * effect chạy `datLoc` và xoá mất `page` — bấm "Sau" xong 250 ms sau tự bật
     * về trang 1.
     *
     * Điều kiện đúng phải hỏi "từ khoá có khác cái đang nằm trên URL không",
     * chứ không phải "effect có chạy không".
     */
    if (tuKhoa === q) return;
    const timer = setTimeout(() => {
      datLoc({ q: tuKhoa || null });
    }, 250);
    return () => clearTimeout(timer);
  }, [search, q, datLoc]);

  /*
   * OQ-251 — `?add=1` mở thẳng hộp thoại thêm tên miền.
   *
   * Nút "Thêm tên miền" ở trang Tổng quan trước đây chỉ điều hướng sang đây, và
   * người dùng phải bấm lần thứ hai. Một nút, hai lần bấm, cùng một ý định.
   *
   * Xoá tham số ngay sau khi mở, bằng `replace` để không thêm một mục vào lịch
   * sử duyệt. Nếu để `add=1` nằm lại trên URL thì đóng hộp thoại rồi F5 nó lại
   * bật ra, và nút Back cũng đưa người dùng về đúng cái vừa đóng.
   */
  useEffect(() => {
    if (params.get('add') !== '1') return;
    if (canAdd) setShowAdd(true);
    vaThamSo({ add: null }, true);
  }, [params, canAdd, vaThamSo]);

  /*
   * Danh sách kết nối cho modal Nhập tên miền (OQ-115). Chỉ nạp khi mở modal —
   * trang này vốn không cần biết kết nối nào, đừng thêm một request cho mọi lần
   * mở trang chỉ vì một nút.
   *
   * OQ-265 thêm một lý do nữa để nạp: khi đang lọc theo kết nối, dải thông báo
   * cần TÊN kết nối chứ không phải UUID. Không lấy tên từ hàng đầu của kết quả
   * được — nếu bộ lọc ra rỗng thì chẳng có hàng nào, mà đó lại đúng lúc người
   * dùng cần biết mình đang lọc theo cái gì.
   */
  const connectionsQuery = useQuery({
    queryKey: ['connections', 'for-import'],
    queryFn: () => api.get<Connection[]>('/connections'),
    enabled: showImport || connectionId !== '',
  });

  const tenKetNoiDangLoc = connectionId
    ? (connectionsQuery.data?.find((c) => c.id === connectionId)?.name ?? null)
    : null;

  const listQuery = useQuery({
    queryKey: ['domains', { status, q, plan, sort, page, perPage, connectionId }],
    queryFn: async () => {
      const search = new URLSearchParams({ sort, page: String(page), perPage: String(perPage) });
      if (status !== 'all') {
        if (status === 'sync_paused') search.set('syncPaused', 'true');
        else search.set('status', status);
      }
      if (q) search.set('q', q);
      if (plan) search.set('plan', plan);
      if (connectionId) search.set('connectionId', connectionId);
      const response = await fetch(`/api/v1/domains?${search.toString()}`, {
        credentials: 'include',
      });
      const payload = (await response.json()) as {
        data?: DomainListResponse;
        meta?: { total: number };
        error?: { code: string; message: string; details?: Record<string, unknown> };
      };
      if (!response.ok) {
        throw new ApiError(
          response.status,
          payload.error?.code ?? 'INTERNAL_ERROR',
          payload.error?.message ?? t('state.error.body'),
          payload.error?.details,
        );
      }
      return { ...payload.data!, total: payload.meta?.total ?? 0 };
    },
  });

  const domains = listQuery.data?.domains ?? [];
  const counts = listQuery.data?.countsByStatus ?? {};

  /*
   * OQ-248 — giữ danh sách gói qua các lần tải lại.
   *
   * Mỗi lần đổi tab hay gõ ô tìm là `queryKey` đổi, và React Query trả
   * `undefined` cho tới khi có dữ liệu mới. Đọc thẳng `data.plans` thì ô chọn
   * **biến mất rồi hiện lại** sau mỗi thao tác — tệ nhất là đúng lúc người dùng
   * đang với chuột tới nó.
   *
   * Danh mục gói gần như không đổi giữa hai lần gọi, nên giữ lại cái vừa thấy
   * là đủ đúng và hết giật.
   */
  const goiDaThay = useRef<string[]>([]);
  if (listQuery.data?.plans) goiDaThay.current = listQuery.data.plans;
  const plans = listQuery.data?.plans ?? goiDaThay.current;
  const total = listQuery.data?.total ?? 0;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['domains'] });
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const sync = useMutation({
    mutationFn: (id: string) => api.post<DomainSyncResult>(`/domains/${id}/sync`),
    onSuccess: async (result) => {
      await invalidate();
      toast(
        t('domains.synced', {
          added: result.records.added,
          updated: result.records.updated,
          removed: result.records.removed,
        }),
      );
    },
    onError,
  });

  const totalAll = Object.values(counts).reduce((sum, value) => sum + value, 0);
  const emptyKind = q || status !== 'all' ? 'filter' : totalAll === 0 ? 'none' : 'none';

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 data-od-id="domains-title">{t('domains.title')}</h1>
          <p className="lede" data-od-id={isAdmin ? 'domains-lede-admin' : 'domains-lede-user'}>
            {isAdmin ? t('domains.ledeAdmin') : t('domains.ledeUser')}
          </p>
        </div>
        <div className="row">
          {/*
            OQ-272 — nút "Đồng bộ Cloudflare" ở đây đã BỎ.

            Nó chỉ gọi `listQuery.refetch()` — tải lại bảng từ CSDL của DNSHub,
            không gửi request nào tới Cloudflare. Nhưng nhãn hứa "Đồng bộ
            Cloudflare", nên bấm xong người dùng có lý do tin rằng 78 tên miền
            vừa được đồng bộ.

            Chủ dự án: *"Tải lại đó là tương tự F5 đúng không? Nếu đúng thì bỏ
            luôn nút cho đơn giản."* — đúng, và F5 thì trình duyệt nào cũng có.
            Một nút chỉ làm lại việc phím F5 đã làm thì không đáng chiếm chỗ,
            càng không đáng mang một cái tên hứa nhiều hơn nó làm.

            Đồng bộ THẬT vẫn còn ở hai chỗ đúng ngữ cảnh: biểu tượng ⟳ trên mỗi
            hàng, và nút ở trang chi tiết tên miền.
          */}
          {canAdd ? (
            <button
              className="btn"
              type="button"
              data-od-id="domains-import"
              onClick={() => setShowImport(true)}
            >
              <Icon name="cloud" size={16} />
              {t('domains.import')}
            </button>
          ) : null}
          {canAdd ? (
            <button
              className="btn btn-primary"
              type="button"
              data-od-id="domains-add"
              onClick={() => setShowAdd(true)}
            >
              <Icon name="plus" size={16} />
              {t('domains.add')}
            </button>
          ) : null}
        </div>
      </div>

      <section className="grid g-4" data-od-id="domains-stats">
        <div className="card stat" data-od-id="stat-total">
          <div className="label">{t('domains.statTotal')}</div>
          <div className="value num">{formatNumber(totalAll)}</div>
          <div className="delta">{t('domains.statTotalNote')}</div>
        </div>
        <div className="card stat" data-od-id="stat-active">
          <div className="label">{t('domains.statActive')}</div>
          <div className="value num">{formatNumber(counts['active'] ?? 0)}</div>
          <div className="delta">{t('domains.statActiveNote')}</div>
        </div>
        <div className="card stat" data-od-id="stat-pending">
          <div className="label">{t('domains.statPending')}</div>
          <div className="value num">{formatNumber(counts['pending'] ?? 0)}</div>
          <div className="delta warn">{t('domains.statPendingNote')}</div>
        </div>
        <div className="card stat" data-od-id="stat-paused">
          <div className="label">{t('domains.statPaused')}</div>
          <div className="value num">{formatNumber(counts['paused'] ?? 0)}</div>
          <div className="delta">{t('domains.statPausedNote')}</div>
        </div>
      </section>

      {connectionId ? (
        <div className="scope-note mt-20" data-od-id="domains-conn-filter">
          <Icon name="cloud" size={17} />
          <span className="grow">
            {t('domains.filteredByConnection', {
              name: tenKetNoiDangLoc ?? t('domains.thisConnection'),
            })}
          </span>
          <button
            className="btn btn-sm"
            type="button"
            onClick={() => datLoc({ connectionId: null })}
            data-od-id="domains-conn-filter-clear"
          >
            {t('domains.clearConnectionFilter')}
          </button>
        </div>
      ) : null}

      {!isAdmin ? (
        <div className="scope-note mt-20" data-od-id="domains-scope-note">
          <Icon name="info" size={17} />
          <span>{t('domains.scopeNote')}</span>
        </div>
      ) : null}

      <section className="card mt-20" data-od-id="domains-table-card">
        <div className="card-head">
          <div className="toolbar grow">
            <div className="filter-pills" data-od-id="domains-filter">
              {FILTERS.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={status === value}
                  onClick={() => datLoc({ status: value === 'all' ? null : value })}
                >
                  {t(
                    value === 'all'
                      ? 'domains.filterAll'
                      : value === 'active'
                        ? 'domains.filterActive'
                        : value === 'pending'
                          ? 'domains.filterPending'
                          : value === 'paused'
                            ? 'domains.filterPaused'
                            : value === 'sync_paused'
                              ? 'domains.filterSyncPaused'
                              : 'domains.filterError',
                  )}
                </button>
              ))}
            </div>
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder={t(
                isAdmin ? 'domains.searchPlaceholderAdmin' : 'domains.searchPlaceholder',
              )}
              odId="domains-search"
            />
            {/*
              OQ-248 — lọc theo gói.
              Chỉ hiện khi có từ hai gói trở lên: một ô chọn duy nhất một lựa
              chọn không lọc được gì, chỉ chiếm chỗ và làm người dùng tưởng
              mình bỏ sót thao tác nào đó.
            */}
            {plans.length > 1 ? (
              <select
                className="lvl loc-gon"
                aria-label={t('domains.filterPlanLabel')}
                data-od-id="domains-plan-filter"
                value={plan}
                onChange={(event) => datLoc({ plan: event.target.value || null })}
              >
                <option value="">{t('domains.filterPlanAll')}</option>
                {plans.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          <span className="small muted num">
            {formatNumber(domains.length)} / {formatNumber(total)}
          </span>
        </div>

        <AsyncSection
          loading={listQuery.isLoading}
          error={listQuery.error}
          data={listQuery.data}
          onRetry={() => void listQuery.refetch()}
          skeleton={<SkeletonCard rows={6} />}
        >
          {() =>
            domains.length === 0 ? (
              <EmptyState
                icon="domains"
                title={
                  emptyKind === 'filter'
                    ? t('domains.emptyFilterTitle')
                    : canAdd
                      ? t('domains.emptyTitle')
                      : t('domains.emptyScopeTitle')
                }
                body={
                  emptyKind === 'filter'
                    ? t('domains.emptyFilterBody')
                    : canAdd
                      ? t('domains.emptyBody')
                      : t('domains.emptyScopeBody')
                }
                action={
                  emptyKind !== 'filter' && canAdd ? (
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => setShowAdd(true)}
                    >
                      {t('domains.add')}
                    </button>
                  ) : undefined
                }
              />
            ) : (
              <>
                <div className="table-wrap">
                  <table className="tbl" id="domains-table" data-od-id="domains-table">
                    <thead>
                      <tr>
                        <th>
                          <SortButton
                            label={t('domains.colName')}
                            field="name"
                            sort={sort}
                            onSort={(value) => datLoc({ sort: value })}
                          />
                        </th>
                        {isAdmin ? (
                          <th>
                            <SortButton
                              label={t('domains.colOwner')}
                              field="owner"
                              sort={sort}
                              onSort={(value) => datLoc({ sort: value })}
                            />
                          </th>
                        ) : null}
                        <th>
                          <SortButton
                            label={t('domains.colStatus')}
                            field="status"
                            sort={sort}
                            onSort={(value) => datLoc({ sort: value })}
                          />
                        </th>
                        <th>
                          <SortButton
                            label={t('domains.colPlan')}
                            field="plan"
                            sort={sort}
                            onSort={(value) => datLoc({ sort: value })}
                          />
                        </th>
                        <th className="right">
                          <SortButton
                            label={t('domains.colRecords')}
                            field="recordCount"
                            sort={sort}
                            onSort={(value) => datLoc({ sort: value })}
                          />
                        </th>
                        <th className="right">
                          <SortButton
                            label={t('domains.colShares')}
                            field="shareCount"
                            sort={sort}
                            onSort={(value) => datLoc({ sort: value })}
                          />
                        </th>
                        <th>
                          <SortButton
                            label={t('domains.colConnection')}
                            field="connection"
                            sort={sort}
                            onSort={(value) => datLoc({ sort: value })}
                          />
                        </th>
                        <th>
                          <SortButton
                            label={t('domains.colUpdated')}
                            field="updatedAt"
                            sort={sort}
                            onSort={(value) => datLoc({ sort: value })}
                          />
                        </th>
                        <th className="right">{t('domains.colActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {domains.map((domain) => (
                        <tr key={domain.id} data-state={domain.status}>
                          <td>
                            <div className="cell-main">
                              <b>
                                <Link
                                  to={`/domains/${domain.id}`}
                                  style={{ textDecoration: 'none' }}
                                >
                                  {domain.name}
                                </Link>
                              </b>
                            </div>
                            <div className="sub mono">
                              {domain.zoneId
                                ? `${domain.zoneId.slice(0, 6)}…${domain.zoneId.slice(-4)}`
                                : '—'}
                            </div>
                          </td>
                          {isAdmin ? (
                            <td>
                              <div className="cell-main">
                                <span
                                  className={`avatar sm ${avatarTone(domain.owner?.id ?? domain.id)}`}
                                >
                                  {initialsOf(
                                    domain.owner?.fullName ?? '',
                                    domain.owner?.email ?? '',
                                  )}
                                </span>
                                <span>{domain.owner?.fullName || domain.owner?.email}</span>
                              </div>
                            </td>
                          ) : null}
                          <td>
                            <span className={`chip ${DOMAIN_STATUS_CHIP[domain.status] ?? ''}`}>
                              <span className="dot" />
                              {t(`domains.status.${domain.status}`)}
                            </span>
                            {domain.syncPaused ? (
                              <span
                                className="chip warn"
                                data-od-id="chip-sync-paused"
                                title={t('detail.syncPausedHint')}
                              >
                                <span className="dot" />
                                {t('detail.syncPaused')}
                              </span>
                            ) : null}
                          </td>
                          <td>
                            <span className="chip">{domain.plan}</span>
                          </td>
                          <td className="right num">{formatNumber(domain.recordCount)}</td>
                          <td className="right num">
                            <ShareCountCell
                              count={domain.shareCount ?? 0}
                              pending={domain.sharePendingCount ?? 0}
                              to={`/domains/${domain.id}?tab=access`}
                              odId={`domain-shares-${domain.name}`}
                            />
                          </td>
                          <td>
                            <span className="small">{domain.connection?.name ?? '—'}</span>
                            {/*
                             * Tài khoản Cloudflare (OQ-117) — đặt DƯỚI tên kết
                             * nối chứ không thành cột riêng: FR-020 chốt danh
                             * sách cột, và một token thường phủ nhiều tài khoản
                             * nên hai thông tin này luôn đi cùng nhau.
                             */}
                            {domain.cfAccountName ? (
                              <div className="small muted" title={domain.cfAccountId ?? undefined}>
                                {domain.cfAccountName}
                              </div>
                            ) : null}
                          </td>
                          <td className="num small">{formatRelative(domain.updatedAt)}</td>
                          <td className="right">
                            <div className="row-actions">
                              <Link className="btn btn-sm" to={`/domains/${domain.id}`}>
                                {t('domains.open')}
                              </Link>
                              <button
                                className="btn btn-sm btn-ghost"
                                type="button"
                                aria-label={t('domains.sync')}
                                title={t('domains.sync')}
                                disabled={sync.isPending}
                                onClick={() => sync.mutate(domain.id)}
                              >
                                <Icon name="refresh" size={15} />
                              </button>
                              {domain.relation === 'owned' || isAdmin ? (
                                <button
                                  className="btn btn-sm btn-danger"
                                  type="button"
                                  aria-label={t('common.delete')}
                                  title={t('common.delete')}
                                  onClick={() => setToDelete(domain)}
                                >
                                  <Icon name="trash" size={15} />
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="card-foot">
                  <span className="pager num">
                    {t('domains.page', { page, total: formatNumber(total) })}
                  </span>
                  <span style={{ flex: 1 }} />
                  <Pagination
                    page={page}
                    perPage={perPage}
                    total={total}
                    odId="domains-pager"
                    onPage={datTrang}
                    onPerPage={datSoDong}
                  />
                </div>
              </>
            )
          }
        </AsyncSection>
      </section>

      {showAdd ? (
        <AddDomainModal
          isAdmin={isAdmin}
          onClose={() => setShowAdd(false)}
          onCreated={async (result) => {
            await invalidate();
            setShowAdd(false);
            setCreated(result);
            toast(t('domains.created', { name: result.domain.name }));
          }}
        />
      ) : null}

      {showImport ? (
        <ImportZonesModal
          connections={connectionsQuery.data ?? []}
          onClose={() => setShowImport(false)}
          onDone={invalidate}
        />
      ) : null}

      {created ? (
        <NameserverModal result={created} onClose={() => setCreated(null)} onRefresh={invalidate} />
      ) : null}

      {toDelete ? (
        <DeleteDomainModal
          domain={toDelete}
          onClose={() => setToDelete(null)}
          onDone={async () => {
            await invalidate();
            setToDelete(null);
            toast(t('domains.deleted'));
          }}
        />
      ) : null}
    </>
  );
}

/* ── Modal: thêm tên miền (FR-024, FR-025, BR-5) ────────────────────── */

/**
 * Xuất ra để `add-domain-error.test.tsx` dựng được modal thật (OQ-121).
 *
 * Không xuất thì test phải đi vòng qua `DomainsPage` — kéo theo truy vấn danh
 * sách, phân trang, quyền — và mỗi lần một trong số đó đổi là test lỗi vì lý
 * do chẳng liên quan gì tới thứ nó canh.
 */
export function AddDomainModal({
  isAdmin,
  onClose,
  onCreated,
}: {
  isAdmin: boolean;
  onClose: () => void;
  onCreated: (result: CreateDomainResponse) => Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [plan, setPlan] = useState('free');
  const [assignToUserId, setAssign] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [cfAccountChon, setCfAccountChon] = useState('');
  const [scanRecords, setScanRecords] = useState(true);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const available = useQuery({
    queryKey: ['connections', 'available', assignToUserId],
    queryFn: () =>
      api.get<AvailableConnections>(
        assignToUserId.trim()
          ? `/connections/available?forUserId=${encodeURIComponent(assignToUserId.trim())}`
          : '/connections/available',
      ),
  });

  const own = useMemo(() => available.data?.connections ?? [], [available.data]);
  const fallback = available.data?.fallbackConnection ?? null;

  useEffect(() => {
    // BR-5: có đúng một kết nối thì chọn sẵn; không có thì để trống (dùng mặc định).
    setConnectionId(own.length === 1 ? own[0]!.id : '');
  }, [own]);

  /*
   * FR-024 [A] — "Gán cho người dùng" là DANH SÁCH CHỌN, không phải ô nhập id.
   * `domains.html:246` dùng `<select id="add-domain-owner">`; bản trước bắt
   * người vận hành gõ UUID của đồng nghiệp. Cùng lỗi đã sửa ở màn kết nối
   * (OQ-111). Chỉ nạp khi là Admin vì `GET /users` đòi `adm.users`.
   */
  const usersQuery = useQuery({
    queryKey: ['users', 'add-domain-owner-options'],
    queryFn: () => api.get<{ items: UserOption[] }>('/users?pageSize=200'),
    enabled: isAdmin,
  });

  /*
   * Tài khoản Cloudflare của kết nối đang chọn — OQ-120.
   *
   * Kết nối phải chốt xong mới hỏi được, vì tài khoản thuộc về TOKEN của kết
   * nối. Kết nối chưa chọn (`''`, nghĩa là dùng mặc định hệ thống) thì không
   * gọi: `chooseConnection` phía máy chủ mới biết rơi về kết nối nào.
   */
  const accountsQuery = useQuery({
    queryKey: ['domains', 'cf-accounts', connectionId],
    queryFn: () =>
      api.get<{ accounts: CfAccountOption[] }>(
        `/domains/cf-accounts?connectionId=${encodeURIComponent(connectionId)}`,
      ),
    enabled: connectionId !== '',
  });

  const accounts = useMemo(() => accountsQuery.data?.accounts ?? [], [accountsQuery.data]);

  /*
   * Giá trị HIỆU DỤNG, không phải `useState` khởi tạo từ dữ liệu chưa tải —
   * đúng bài học OQ-116: `useState(accounts[0]?.id)` chạy ở lần render đầu khi
   * `accounts` còn rỗng, nên state ở lại `''` trong khi `<select>` vẫn hiển
   * thị mục đầu. Trông như đã chọn, gửi đi thì rỗng.
   */
  const cfAccountId = cfAccountChon || accounts[0]?.id || '';
  const account = accounts.find((a) => a.id === cfAccountId) ?? null;

  const create = useMutation({
    mutationFn: () =>
      api.post<CreateDomainResponse>('/domains', {
        name,
        plan,
        scanRecords,
        connectionId: connectionId || null,
        cfAccountId: cfAccountId || null,
        assignToUserId: assignToUserId.trim() || null,
      }),
    onSuccess: onCreated,
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  const noConnection = own.length === 0 && !fallback;

  return (
    <Modal
      open
      title={t('domains.addTitle')}
      subtitle={t('domains.addSub')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => create.mutate()}
      footer={
        <>
          <span className="grow small muted">{t('domains.addStep')}</span>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={create.isPending || noConnection}
          >
            {create.isPending ? t('common.processing') : t('domains.add')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />

      <Field
        label={t('domains.nameLabel')}
        placeholder={t('domains.namePlaceholder')}
        hint={t('domains.nameHint')}
        value={name}
        onChange={(event) => setName(event.target.value)}
        error={error?.fieldMessage('name')}
        required
      />

      <div className="grid g-2">
        <SelectField
          label={t('domains.planLabel')}
          value={plan}
          onChange={(event) => setPlan(event.target.value)}
        >
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="business">Business</option>
        </SelectField>
        {isAdmin ? (
          <SelectField
            label={t('domains.assignLabel')}
            hint={t('domains.assignHint')}
            value={assignToUserId}
            onChange={(event) => setAssign(event.target.value)}
            error={error?.fieldMessage('assignToUserId')}
          >
            <option value="">{t('domains.assignSelf')}</option>
            {(usersQuery.data?.items ?? []).map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName ? `${user.fullName} · ${user.email}` : user.email}
              </option>
            ))}
          </SelectField>
        ) : null}
      </div>

      <SelectField
        label={t('domains.connectionLabel')}
        value={connectionId}
        disabled={own.length === 0}
        onChange={(event) => setConnectionId(event.target.value)}
        error={error?.fieldMessage('connectionId')}
        hint={
          own.length === 0
            ? t('domains.connectionHintNone')
            : own.length === 1
              ? t(assignToUserId ? 'domains.connectionHintOne' : 'domains.connectionHintOneSelf')
              : t('domains.connectionHintMany')
        }
      >
        {own.length === 0 ? (
          <option value="">{fallback?.name ?? t('common.none')}</option>
        ) : (
          <>
            {own.length > 1 ? <option value="">—</option> : null}
            {own.map((connection) => (
              <option key={connection.id} value={connection.id}>
                {connection.name}
                {connection.isSystemDefault ? ` · ${t('conn.default')}` : ''}
              </option>
            ))}
          </>
        )}
      </SelectField>

      {/* FR-025 — chưa có kết nối riêng.

          Câu chữ đổi theo NGƯỜI SẼ SỞ HỮU tên miền, không theo người đang ngồi
          gõ (OQ-326). "Người này chưa có kết nối" chỉ đúng khi quản trị viên
          đang thêm hộ; người tự thêm cho mình đọc câu ấy sẽ tưởng hệ thống đang
          nói về ai khác. */}
      {own.length === 0 && fallback ? (
        <div className="scope-note" style={{ marginBottom: 2 }}>
          <Icon name="info" size={17} />
          <span>
            {t(assignToUserId ? 'domains.connectionFallback' : 'domains.connectionFallbackSelf', {
              name: fallback.name,
            })}{' '}
            <Link to="/connections">{t('domains.connectionAdd')}</Link>
          </span>
        </div>
      ) : null}
      {noConnection ? (
        <div className="scope-note warn">
          <Icon name="warn" size={17} />
          <span>
            {t('domains.connectionNone')}{' '}
            <Link to="/connections">{t('domains.connectionAdd')}</Link>
          </span>
        </div>
      ) : null}

      {/*
       * Tài khoản Cloudflare — OQ-120.
       *
       * Chỉ hiện khi token phủ TỪ HAI tài khoản trở lên. Một tài khoản thì ô
       * chọn không có gì để chọn, chỉ thêm một bước bấm; tên tài khoản vẫn
       * hiện ở khối nameserver bên dưới nên người dùng không mất thông tin.
       */}
      {accounts.length > 1 ? (
        <SelectField
          label={t('domains.cfAccountLabel')}
          hint={
            account && account.nameServers.length > 0
              ? t('domains.cfAccountNsHint', { ns: account.nameServers.join(', ') })
              : t('domains.cfAccountHint')
          }
          value={cfAccountId}
          onChange={(event) => setCfAccountChon(event.target.value)}
          error={error?.fieldMessage('cfAccountId')}
        >
          {accounts.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
              {item.zoneCount > 0 ? ` · ${t('domains.cfAccountZones', { n: item.zoneCount })}` : ''}
            </option>
          ))}
        </SelectField>
      ) : null}

      {/*
       * KHÔNG hiện nameserver ở bước 1 — quyết định của chủ dự án, và nó đúng
       * hơn cách tôi làm ban đầu.
       *
       * `domains.html:265-269` đặt khối nameserver trong modal thêm tên miền,
       * nên tôi dựng theo. Nhưng bản mẫu là TĨNH: nó không có bước 2, nên phải
       * nhét nameserver vào đâu đó. Hệ thật có `NameserverModal` (FR-026) hiện
       * nameserver **thật** từ phản hồi `POST /zones`.
       *
       * Đặt ở bước 1 thì hai giá trị cùng tồn tại: một cái đoán được, một cái
       * chắc chắn. Người dùng nhìn thấy cái đoán trước, và cặp nameserver
       * trông đủ chính thức để họ sao chép ngay mà không đợi bước 2 — đúng
       * cái bẫy mà toàn bộ phần chú thích "30/31" sinh ra để tránh. Bỏ hẳn thì
       * không còn gì để hiểu nhầm.
       *
       * Thống kê nameserver theo tài khoản (OQ-120) KHÔNG mất: nó chuyển thành
       * chú thích của ô chọn tài khoản, nơi nó thật sự có ích — giúp phân biệt
       * hai tài khoản khi tên chúng na ná nhau.
       */}

      <div className="setting-row" style={{ paddingTop: 4 }}>
        <div className="grow">
          <b>{t('domains.scanRecords')}</b>
          <div className="sub">{t('domains.scanRecordsSub')}</div>
        </div>
        <label className="switch" aria-label={t('domains.scanRecords')}>
          <input
            type="checkbox"
            checked={scanRecords}
            onChange={(event) => setScanRecords(event.target.checked)}
          />
          <span className="track" />
        </label>
      </div>
    </Modal>
  );
}

/* ── Modal: hướng dẫn nameserver (FR-026) ───────────────────────────── */

/**
 * Một dòng `nameserver + nút Sao chép` — `domains.html:267`.
 *
 * Dùng ở hai chỗ trong tệp này: bản xem trước lúc thêm tên miền (OQ-120) và
 * màn xác minh nameserver sau khi tạo (FR-026). Hai chỗ PHẢI giống hệt nhau —
 * người dùng đối chiếu con số ở bước 2 với con số họ vừa thấy ở bước 1, nên
 * khác nhau một chi tiết trình bày là đủ khiến họ nghi mình chép nhầm.
 */
function CopyLine({ value }: { value: string }) {
  const t = useT();
  return (
    <div className="copy-line">
      <span className="grow mono">{value}</span>
      <button
        className="btn btn-sm btn-ghost"
        type="button"
        onClick={() => void navigator.clipboard?.writeText(value)}
      >
        {t('common.copy')}
      </button>
    </div>
  );
}

function NameserverModal({
  result,
  onClose,
  onRefresh,
}: {
  result: CreateDomainResponse;
  onClose: () => void;
  onRefresh: () => Promise<unknown>;
}) {
  const t = useT();
  const { toast, toastError } = useToast();
  const [state, setState] = useState<CreateDomainResponse>(result);

  const recheck = useMutation({
    mutationFn: () => api.post<VerifyNsResult>(`/domains/${state.domain.id}/verify-ns`),
    onSuccess: async (verified) => {
      setState((prev) => ({ ...prev, domain: verified.domain }));
      await onRefresh();
      if (verified.activated) toast(t('domains.nsActive'));
      else toastError(t('domains.nsPending'));
    },
    onError: (err: unknown) =>
      toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  return (
    <Modal
      open
      title={t('domains.nsTitle')}
      subtitle={t('domains.nsSub')}
      onClose={onClose}
      footer={
        <>
          <button
            className="btn"
            type="button"
            onClick={() => recheck.mutate()}
            disabled={recheck.isPending}
          >
            <Icon name="refresh" size={16} />
            {t('domains.nsRecheck')}
          </button>
          <Link className="btn btn-primary" to={`/domains/${state.domain.id}`}>
            {t('domains.open')}
          </Link>
        </>
      }
    >
      <div className="field">
        <label>{t('domains.nsLabel')}</label>
        {state.nameServers.map((ns) => (
          <CopyLine key={ns} value={ns} />
        ))}
      </div>
      <div className={state.domain.status === 'active' ? 'scope-note' : 'scope-note warn'}>
        <Icon name={state.domain.status === 'active' ? 'check' : 'warn'} size={17} />
        <span>
          {state.domain.status === 'active' ? t('domains.nsActive') : t('domains.nsPending')}
        </span>
      </div>
    </Modal>
  );
}

/* ── Modal: gỡ tên miền (FR-027) ────────────────────────────────────── */

export function DeleteDomainModal({
  domain,
  onClose,
  onDone,
}: {
  domain: Domain;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const t = useT();
  const [confirmName, setConfirmName] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const remove = useMutation({
    mutationFn: () => api.del<void>(`/domains/${domain.id}`, { confirmName }),
    onSuccess: onDone,
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('domains.deleteTitle')}
      subtitle={domain.name}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => remove.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-danger"
            type="submit"
            disabled={remove.isPending || confirmName.trim().toLowerCase() !== domain.name}
          >
            {remove.isPending ? t('common.processing') : t('common.delete')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />
      <div className="scope-note warn">
        <Icon name="warn" size={17} />
        <span>{t('domains.deleteWarn')}</span>
      </div>
      <Field
        label={t('domains.deleteConfirm')}
        placeholder={domain.name}
        autoComplete="off"
        value={confirmName}
        onChange={(event) => setConfirmName(event.target.value)}
        error={error?.fieldMessage('confirmName')}
      />
    </Modal>
  );
}
